import {
  LayoutResponsive,
  getDefaultContext as defaultResponsiveContext,
} from '@dustin-web/microsite-config'
import React, { createContext, Dispatch, useContext } from 'react'
import {
  LayoutHeader,
  getDefaultContext as defaultHeaderContext,
  LayoutHeaderContext,
  LayoutHeaderProvider,
  LayoutHeaderAction,
} from './layout-header-context'
import { LayoutResponsiveContext, LayoutResponsiveProvider } from './layout-responsive-context'
import { ScrollLockProvider } from './scroll-lock-context'

export { LayoutHeaderActionType } from './layout-header-context'

export type LayoutContextType = {
  responsive: LayoutResponsive
  header: LayoutHeader
  dispatchHeader: Dispatch<LayoutHeaderAction>
}

const initialState: LayoutContextType = {
  responsive: defaultResponsiveContext(),
  header: defaultHeaderContext(),
  dispatchHeader: () => null,
}

export const LayoutContext = createContext<LayoutContextType>(initialState)

/**
 * Helper context provider that uses all layout sub contexts.
 */
const LayoutProviderInner = ({ children }: { children?: React.ReactNode }) => {
  const responsive = useContext(LayoutResponsiveContext)
  const { header, dispatchHeader } = useContext(LayoutHeaderContext)

  const state = {
    responsive,
    header,
    dispatchHeader,
  }

  return <LayoutContext.Provider value={state}>{children}</LayoutContext.Provider>
}

/**
 * Context provider that wraps all layout sub contexts.
 * * Note that LayoutHeaderContext requires LayoutResponsiveContext.
 */
export const LayoutProvider = ({ children }: { children?: React.ReactNode }) => {
  return (
    <LayoutResponsiveProvider>
      <LayoutHeaderProvider>
        <LayoutProviderInner>
          <ScrollLockProvider>{children}</ScrollLockProvider>
        </LayoutProviderInner>
      </LayoutHeaderProvider>
    </LayoutResponsiveProvider>
  )
}

/**
 * Context provider that wraps only the responsive context, used in SimpleRoot.
 */
export const SimpleLayoutProvider = ({ children }: { children?: React.ReactNode }) => {
  return (
    <LayoutResponsiveProvider>
      <LayoutProviderInner>{children}</LayoutProviderInner>
    </LayoutResponsiveProvider>
  )
}

export const useLayout = () => {
  return useContext(LayoutContext)
}
